// src/pages/Dashboard/Reports/DriverAvailabilityReports.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Activity, CheckCircle, Eye, Filter, Loader2, Users, X, XCircle } from "lucide-react";

import { ReportsService } from "../../../services/reports.service";
import { EmptyReport, ReportCard, ReportPanel } from "./components/ReportCards";
import TrendBars from "./components/TrendBars";
import { dt, n, td, th } from "./components/reportFormat";

function statusBadge(isAvailable) {
  const ok = isAvailable === true || String(isAvailable).toLowerCase() === "true";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
    >
      {ok ? "Available" : "Unavailable"}
    </span>
  );
}

function text(v) {
  const s = String(v ?? "").trim();
  return s || "-";
}

export default function DriverAvailabilityReports({ filters }) {
  const [summary, setSummary] = useState({});
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState("all");
  const [error, setError] = useState("");

  const [selectedRow, setSelectedRow] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const combinedFilters = {
          ...filters,
          availabilityStatus,
        };

        const [summaryRes, reportRes] = await Promise.all([
          ReportsService.driverAvailabilitySummary(combinedFilters),
          ReportsService.driverAvailability(combinedFilters),
        ]);

        if (!mounted) return;

        const summaryData = summaryRes?.data?.data || summaryRes?.data || summaryRes || {};
        const reportRows = reportRes?.data?.data || reportRes?.data?.rows || reportRes?.rows || reportRes?.data || [];

        setSummary(summaryData);
        setReportData(Array.isArray(reportRows) ? reportRows : []);
      } catch (err) {
        console.error("Driver Availability Error:", err);
        if (!mounted) return;
        setSummary({});
        setReportData([]);
        setError(err?.response?.data?.message || err?.message || "Failed to load driver availability report");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [filters, availabilityStatus]);

  const stats = useMemo(
    () => ({
      totalChecks: Number(summary?.total_checks || 0),
      availableChecks: Number(summary?.available_checks || 0),
      unavailableChecks: Number(summary?.unavailable_checks || 0),
      uniqueUsers: Number(summary?.unique_users || 0),
    }),
    [summary]
  );

  const trendRows = reportData.map((r) => ({
    bucket: r.last_checked,
    total_checks: Number(r.total_checks || 0),
  }));

  async function openHistory(row) {
    if (!row?.user_id) return;

    try {
      setSelectedRow(row);
      setHistoryRows([]);
      setHistoryError("");
      setHistoryLoading(true);

      const historyFilters = {
        ...filters,
        availabilityStatus: "all",
        limit: 100,
      };

      if (row.warehouse_id) {
        historyFilters.warehouseId = row.warehouse_id;
      }

      const res = await ReportsService.driverAvailabilityUserHistory(row.user_id, historyFilters);
      const rows = res?.data?.data || res?.data?.rows || res?.rows || res?.data || [];
      setHistoryRows(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Driver Availability History Error:", err);
      setHistoryError(err?.response?.data?.message || err?.message || "Failed to load user availability history");
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  function closeHistory() {
    setSelectedRow(null);
    setHistoryRows([]);
    setHistoryError("");
    setHistoryLoading(false);
  }

  if (loading && reportData.length === 0) {
    return <div className="py-14 text-center font-bold text-[#d86a78]">Loading driver availability report...</div>;
  }

  return (
    <div className="space-y-5">
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={Activity} title="Total Checks" value={n(stats.totalChecks)} />
        <ReportCard icon={CheckCircle} title="Available" value={n(stats.availableChecks)} />
        <ReportCard icon={XCircle} title="Unavailable" value={n(stats.unavailableChecks)} />
        <ReportCard icon={Users} title="Unique Users" value={n(stats.uniqueUsers)} />
      </div>

      <TrendBars rows={trendRows} field="total_checks" label="Driver Availability Trend" subtitle="Driver availability requests over time" />

      <ReportPanel
        title="Driver Availability Checks"
        subtitle="Each row is grouped by user and warehouse. Click any row to see every check status with exact time."
      >
        <div className="mb-4 flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs font-bold text-slate-500">
            Total Checks column shows how many times the same user checked driver availability.
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Availability Status:</span>
            <select
              value={availabilityStatus}
              onChange={(e) => setAvailabilityStatus(e.target.value)}
              className="ml-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-[#d86a78] focus:ring-1 focus:ring-[#d86a78]"
            >
              <option value="all">All Records</option>
              <option value="available">Users who found drivers Available</option>
              <option value="unavailable">Users who found drivers Unavailable</option>
            </select>
          </div>
        </div>

        {!reportData.length ? (
          <EmptyReport />
        ) : (
          <div className="max-h-[560px] overflow-auto rounded-[14px] border border-[#f7d8dc]">
            <table className="min-w-full border-collapse bg-white">
              <thead className="sticky top-0 z-10 bg-[#f5cfd5]">
                <tr>
                  <th className={th}>User</th>
                  <th className={th}>Phone</th>
                  <th className={th}>Warehouse</th>
                  <th className={th}>Address</th>
                  <th className={th}>Total Checks</th>
                  <th className={th}>Available</th>
                  <th className={th}>Unavailable</th>
                  <th className={th}>Latest Status</th>
                  <th className={th}>Last Checked</th>
                  <th className={th}>History</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reportData.map((row, idx) => (
                  <tr
                    key={`${row.user_id || "user"}-${row.warehouse_id || "warehouse"}-${idx}`}
                    onClick={() => openHistory(row)}
                    className="cursor-pointer transition hover:bg-[#fff8f9]"
                  >
                    <td className={`${td} min-w-[160px] font-black text-slate-800`}>{text(row.user_name || row.user_phone || "Unknown User")}</td>
                    <td className={td}>{text(row.user_phone)}</td>
                    <td className={td}>{text(row.warehouse_name)}</td>
                    <td className={`${td} min-w-[260px] max-w-[420px]`}>{text(row.full_address)}</td>
                    <td className={`${td} text-center`}>
                      <span className="inline-flex rounded-full bg-[#fff1f3] px-3 py-1 text-xs font-black text-[#d86a78]">
                        {n(row.total_checks)}
                      </span>
                    </td>
                    <td className={td}>{n(row.available_count)}</td>
                    <td className={td}>{n(row.unavailable_count)}</td>
                    <td className={td}>{statusBadge(row.latest_is_available ?? row.has_available)}</td>
                    <td className={td}>{dt(row.last_checked)}</td>
                    <td className={td}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openHistory(row);
                        }}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#f1c4ca] px-2.5 py-1 text-[11px] font-black text-[#d86a78] hover:bg-[#fff1f3]"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportPanel>

      {selectedRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[88vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-[#f4cdd2] bg-[#fff8f9] px-5 py-4">
              <div>
                <h3 className="text-base font-black text-slate-900">Availability Check History</h3>
                <p className="mt-1 text-xs font-bold text-slate-500">
                  {text(selectedRow.user_name || selectedRow.user_phone || "Unknown User")} · {text(selectedRow.warehouse_name)}
                </p>
                <p className="mt-1 text-[11px] font-semibold text-slate-400">
                  Showing every driver availability result with the time it was checked.
                </p>
              </div>
              <button
                type="button"
                onClick={closeHistory}
                className="rounded-full border border-[#f1c4ca] bg-white p-2 text-slate-500 hover:bg-[#fff1f3] hover:text-[#d86a78]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 border-b border-slate-100 px-5 py-4 sm:grid-cols-3">
              <div className="rounded-xl bg-slate-50 px-4 py-3">
                <div className="text-[10px] font-black uppercase text-slate-400">Total Checks</div>
                <div className="mt-1 text-xl font-black text-slate-900">{n(selectedRow.total_checks)}</div>
              </div>
              <div className="rounded-xl bg-emerald-50 px-4 py-3">
                <div className="text-[10px] font-black uppercase text-emerald-600">Available</div>
                <div className="mt-1 text-xl font-black text-emerald-700">{n(selectedRow.available_count)}</div>
              </div>
              <div className="rounded-xl bg-red-50 px-4 py-3">
                <div className="text-[10px] font-black uppercase text-red-600">Unavailable</div>
                <div className="mt-1 text-xl font-black text-red-700">{n(selectedRow.unavailable_count)}</div>
              </div>
            </div>

            <div className="max-h-[55vh] overflow-auto p-5">
              {historyError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {historyError}
                </div>
              ) : null}

              {historyLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm font-black text-[#d86a78]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading history...
                </div>
              ) : !historyRows.length ? (
                <EmptyReport text="No check history found for this user" />
              ) : (
                <div className="overflow-auto rounded-[14px] border border-[#f7d8dc]">
                  <table className="min-w-full border-collapse bg-white">
                    <thead className="sticky top-0 z-10 bg-[#f5cfd5]">
                      <tr>
                        <th className={th}>#</th>
                        <th className={th}>Type</th>
                        <th className={th}>Status</th>
                        <th className={th}>Total</th>
                        <th className={th}>Checked Time</th>
                        <th className={th}>Reason</th>
                        <th className={th}>Warehouse</th>
                        <th className={th}>Address</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {historyRows.map((row, idx) => (
                        <tr key={row.id || idx} className="transition hover:bg-[#fff8f9]">
                          <td className={td}>{idx + 1}</td>
                          <td className={td}>
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${row.is_summary ? "bg-slate-100 text-slate-700" : "bg-blue-50 text-blue-700"}`}>
                              {row.is_summary ? "Daily Summary" : "Raw Check"}
                            </span>
                          </td>
                          <td className={td}>{statusBadge(row.is_available)}</td>
                          <td className={td}>{row.is_summary ? n(row.total_checks) : 1}</td>
                          <td className={`${td} min-w-[150px]`}>{row.is_summary ? text(row.summary_date) : dt(row.created_at)}</td>
                          <td className={`${td} min-w-[220px]`}>
                            {row.is_summary
                              ? `Available ${n(row.available_count)} / Unavailable ${n(row.unavailable_count)}`
                              : text(row.reason_message || row.reason_code)}
                          </td>
                          <td className={td}>{text(row.warehouse_name)}</td>
                          <td className={`${td} min-w-[260px] max-w-[480px]`}>{text(row.full_address)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
